import Button from "components/atoms/Button";
import Text from "components/atoms/Text";
import Textfield from "components/atoms/Textfield";
import UIPlaceholder from "components/atoms/UIPlaceholder";
import Fieldrow, { Fieldcell } from "components/molecules/Fieldrow";
import Pagination from "components/molecules/Pagination";
import { Tab } from "components/molecules/Tabs";
import Modal from "components/organisms/Modal";
import colors, { alphaColor } from "constants/colors";
import { maxQuery, minQuery, SizeMax, SizeMin } from "constants/media";
import styled, { css, SimpleInterpolation } from "styled-components/macro";
import deActiveStarIcon from "./../../../images/icons/star-light-gray.svg";
import activeStarIcon from "./../../../images/icons/star-orange.svg";

export const flexCss = css`
  display: flex;
  align-items: center;
`;

export const CustomerReviewModal = styled(Modal)<{ isSafari?: boolean }>`
  padding: 0 !important;

  ${({ isSafari }) =>
    isSafari &&
    css`
      padding-top: 35px !important;
      padding-bottom: 35px !important;
    `}
`;

export const FieldCellExtended = styled(Fieldcell)`
  display: flex;
  flex-direction: column;
`;

export const FieldCellRightText = styled(FieldCellExtended)`
  align-items: flex-end;

  > .count-comment-character {
    align-self: flex-end;
    color: ${colors.spanishGray};
    font-size: 12px;
    margin-top: 2px;
  }
`;

export const FieldcellExtended = styled(Fieldcell)`
  ${minQuery(
    SizeMin.Small,
    css`
      margin-bottom: 0 !important;
    `,
  )}

  ${maxQuery(
    SizeMax.Small,
    css`
      &:last-of-type {
        margin-bottom: 0 !important;
      }
    `,
  )}
`;

export const FieldRowExtended = styled(Fieldrow)`
  width: 100%;

  ${maxQuery(
    SizeMax.Small,
    css`
      margin-bottom: 0 !important;
    `,
  )}
`;

export const ReviewGrid = styled.div`
  padding-top: 32px;
`;

export const ReviewContainer = styled.div`
  width: 100%;
`;

export const ReviewHeading = styled.div`
  ${flexCss};
  justify-content: space-between;

  ${maxQuery(
    SizeMax.Small,
    css`
      flex-direction: column;
      align-items: flex-start;
    `,
  )}
`;

export const VideoPanel = styled.div<{ ratioAt?: SizeMin }>`
  width: 60px;
  height: 60px;
  background-color: ${colors.black};

  ${({ ratioAt }): SimpleInterpolation =>
    ratioAt &&
    minQuery(
      ratioAt,
      css`
        width: 182px;
        height: 182px;
      `,
    )};
`;

export const ReviewBody = styled.div`
  margin-top: 32px;

  ${minQuery(
    SizeMin.Large,
    css`
      box-shadow: 0 0 1px 3px ${alphaColor.black0035};
      padding: 20px;
    `,
  )}

  ${minQuery(
    SizeMin.ExtraLarge,
    css`
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      box-shadow: 0 0 1px 3px ${alphaColor.black0035};
      padding: 20px;
      margin-bottom: 32px;
    `,
  )}
`;

export const ReviewTitle = styled.div`
  color: #1e1e20;
  font-size: 5vw;
  font-weight: bold;

  ${minQuery(
    SizeMin.Large,
    css`
      font-size: 2rem;
    `,
  )}

  ${maxQuery(
    SizeMax.Small,
    css`
      margin-bottom: 1em;
    `,
  )}
`;

export const RatingContainer = styled.div`
  ${minQuery(
    SizeMin.ExtraLarge,
    css`
      width: calc(50% - 45px);
    `,
  )}
`;

export const RatingHeading = styled.div`
  ${flexCss};

  ${maxQuery(
    SizeMax.XSmall,
    css`
      flex-direction: column;
    `,
  )}

  ${maxQuery(
    SizeMax.Large,
    css`
      margin-bottom: 1em;
    `,
  )}
`;

export const RatingValue = styled.div<{ value: number }>`
  font-size: 24px;
  font-weight: bold;
  margin-right: 0.5em;

  ${({ value }) =>
    value > 0
      ? css`
          color: ${colors.tangerine};
        `
      : css`
          color: ${colors.platinum};
        `}

  ${minQuery(
    SizeMin.Large,
    css`
      font-size: 32px;
    `,
  )}
`;

export const RatingCounterLabel = styled.div`
  color: #1e1e20;
`;

export const UIProgress = styled.div`
  ${flexCss};
`;

export const UIProgressLine = styled.div`
  width: calc(100% - 120px);
`;

export const ProgressLine = styled.div`
  border-radius: 10px;
  height: 8px;
  background-color: #e7e7e7;
`;

export const ProgressFill = styled.div<{
  value: number | string;
  color: string;
}>`
  border-radius: 10px;
  background-color: ${({ color }) => color};
  height: 8px;
  width: ${({ value }) => (typeof value === "number" ? `${value}%` : value)};
`;

export const UIReviewStat = styled.div`
  margin-top: 1em;

  &:first-of-type {
    margin-top: 0;
  }
`;

export const UIReviewStatTitle = styled.div`
  color: ${colors.dimGray};
`;

export const UIProgressStatValue = styled.div`
  ${flexCss};
  color: ${colors.richElectricBlue};
`;

export const UIProgressStatValueStat = styled.div`
  font-weight: bold;
`;

export const UIProgressStatValueReview = styled.div`
  white-space: nowrap;
`;

export const RatingBody = styled.div`
  ${UIProgressStatValue} {
    min-width: 138px;
    max-width: 138px;
    justify-content: flex-end;
  }
`;

export const CriteriaContainer = styled.div`
  ${minQuery(
    SizeMin.ExtraLarge,
    css`
      width: calc(50% - 45px);
    `,
  )}
`;

export const CriteriaHeading = styled.div`
  ${flexCss};
  min-height: 48px;
  font-weight: bold;

  ${maxQuery(
    SizeMax.Large,
    css`
      margin-top: 0.5em;
    `,
  )}
`;

export const CriteriaBody = styled.div`
  ${UIProgressStatValue} {
    min-width: 98px;
    max-width: 98px;
  }
`;

export const GridHeading = styled.div`
  font-size: 24px;
  font-weight: bold;
  color: ${colors.jet};
`;

type ClientUploadImageListType = {
  wrap?: "1" | "0";
  justifyContent?: "flex-start" | "space-between";
};
export const ClientUploadImageList = styled.div<ClientUploadImageListType>`
  ${flexCss};
  width: 100%;
  flex-wrap: ${({ wrap }) => (wrap === "1" ? "wrap" : "nowrap")};
  justify-content: ${({ justifyContent }) => justifyContent};
  margin-top: 20px;

  ${VideoPanel} {
    ${maxQuery(
      SizeMax.Small,
      css`
        width: 100%;
        height: 100%;
      `,
    )}
  }
`;

export const ClientUploadImageItem = styled.div<{ src: string }>`
  transition: .45s;
  width: 100%;
  height: 100%;
  background: url('${({ src }) => src}') no-repeat center center / cover;
  `;

type ClientUploadImageType = {
  isLast?: boolean;
  isVideoItem?: boolean;
};
export const ClientUploadImage = styled.div<ClientUploadImageType>`
  position: relative;
  cursor: pointer;
  transition: 0.45s;
  overflow: hidden;
  margin-bottom: 0.5em;
  margin-right: 0.5em;

  &:last-of-type {
    margin-right: 0;
  }

  ${({ isLast }) => {
    if (isLast) {
      return css`
        position: relative;
        overflow: hidden;

        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        color: white;

        > span {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          z-index: 2;
        }

        &:before {
          content: "";
          position: absolute;
          top: 50%;
          left: 50%;
          width: 100%;
          height: 100%;
          transform: translate(-50%, -50%);
          background-color: ${alphaColor.black0035};
          transition: 0.45s;
          z-index: 1;
        }

        &:hover {
          &:before {
            opacity: 0.75;
          }
        }
      `;
    }
  }}

  &:hover {
    ${ClientUploadImageItem} {
      opacity: 0.75;
    }
  }

  ${minQuery(
    SizeMin.ExtraLarge,
    css`
      margin-bottom: 22px;
      margin-right: 22px;
    `,
  )}
`;

export const ClientUploadImagePreviewItem = styled(ClientUploadImage)`
  ${({ isVideoItem }) =>
    isVideoItem
      ? css`
          width: auto;
          height: auto;
        `
      : css`
          width: 182px;
          height: 182px;
        `};
`;

export const ClientUploadImageDetailItem = styled(ClientUploadImage)`
  ${({ isVideoItem }) =>
    isVideoItem
      ? css`
          width: auto;
          height: auto;
        `
      : css`
          width: 60px;
          height: 60px;

          ${minQuery(
            SizeMin.Medium,
            css`
              width: 182px;
              height: 182px;
            `,
          )}
        `};
`;

export const GridReviewChatHeading = styled(GridHeading)`
  justify-content: space-between;
  width: 100%;

  ${minQuery(
    SizeMin.Large,
    css`
      ${flexCss};
    `,
  )}
`;

export const GridReviewChatTitle = styled(GridHeading)`
  ${maxQuery(
    SizeMax.Large,
    css`
      margin-bottom: 0.5em;
    `,
  )}
`;

export const TabList = styled.div`
  ${flexCss};

  ${maxQuery(
    SizeMax.Small,
    css`
      justify-content: flex-start;
    `,
  )}
`;

export const TabListItem = styled.div`
  ${flexCss};
  width: 66px;
  height: 23px;
  font-size: 12px;
  color: ${colors.richElectricBlue};
  background-color: white;
  border: 1px solid ${colors.richElectricBlue};
  border-radius: 15px;
  transition: 0.45s;
  cursor: pointer;
  margin-left: 0.25em;
  justify-content: center;

  ${maxQuery(
    SizeMax.Small,
    css`
      margin-bottom: 1em;
    `,
  )}

  ${minQuery(
    SizeMin.Small,
    css`
      margin-left: 25px;
    `,
  )}

  &:first-of-type {
    margin-left: 0;
  }

  &.active,
  &:hover {
    color: white;
    background-color: ${colors.richElectricBlue};
  }
`;

export const ClientCommentReviews = styled.div`
  width: 100%;
  margin-bottom: 1em;
`;

export const ClientCommentSearchBar = styled.div`
  display: inline-flex;
  align-items: center;
  margin-top: 30px;

  .search-label {
    margin-right: 1.125em;
    margin-bottom: 0;
  }

  ${maxQuery(
    SizeMax.Small,
    css`
      flex-direction: column;
      align-items: flex-start;
    `,
  )}
`;

export const ClientCommentReviewTitle = styled.h3`
  font-size: 16px;
  color: ${colors.jet};
  font-weight: bold;

  ${minQuery(
    SizeMin.Small,
    css`
      ${flexCss};
    `,
  )}
`;

export const ClientCommentReviewItem = styled.div`
  margin-top: 14px;
`;

export const ClientCommentReviewItemInner = styled(ClientCommentReviewItem)`
  ${ClientCommentReviewTitle} {
    ${flexCss};
  }
`;

export const ClientCommentReviewWrap = styled.div`
  margin-top: 1em;
  padding-top: 0.5em;

  ${ClientCommentReviewItem} {
    ${ClientCommentReviewItem} {
      padding-left: 15px;

      ${minQuery(
        SizeMin.Small,
        css`
          padding-left: 102px;
        `,
      )}
    }
  }
`;

export const VerifyBuyAtDaikinEShop = styled.p<{
  icon: string;
  isAdmin?: boolean;
}>`
  ${flexCss};
  color: ${({ isAdmin }) => (isAdmin ? colors.richElectricBlue : "#68b022")};
  font-size: 9px;
  font-weight: normal;
  background: url("${({ icon }) => icon}")
  no-repeat center left / 9px 9px;
  margin-bottom: 0;
  padding-left: 12px;
  
  ${minQuery(
    SizeMin.Small,
    css`
      font-size: 14px;
      background-size: 12px;
      padding-left: 16px;
    `,
  )}
`;

export const TagReview = styled.span`
  display: inline-block;
  background-color: ${alphaColor.richElectricBlue01};
  border: 1px solid ${colors.richElectricBlue};
  border-radius: 2px;
  padding: 0.5em;
  color: ${colors.richElectricBlue};
  font-weight: normal;
  font-size: 12px;
  margin-left: 0.25em;

  ${maxQuery(
    SizeMax.Small,
    css`
      font-size: 8px;

      &:first-of-type {
        margin-left: 0;
      }
    `,
  )}
`;

export const LeftCLientCommentReview = styled.div`
  display: flex;

  ${maxQuery(
    SizeMax.Small,
    css`
      width: 100%;
    `,
  )}
`;

export const TagViews = styled.div`
  ${maxQuery(
    SizeMax.Small,
    css`
      width: 100%;
      margin-top: 0.5em;
    `,
  )}
`;

export const ListTagViewInner = styled.div``;
export const ListTagView = styled.div`
  margin-bottom: 1em;
  padding: 1em;

  ${ListTagViewInner} {
    margin: -1em;
  }
  ${TagReview} {
    margin: 0.75em;
  }
`;

export const RcRatingWrapper = styled.div`
  min-width: 100px;
  margin-bottom: -2px;
  min-width: 120px;
`;

export const ClientCommentReviewContent = styled.div`
  font-size: 14px;

  &:after {
    content: "";
    display: block;
    clear: both;
  }

  > span {
    color: ${colors.jet};
  }

  ${minQuery(
    SizeMin.Medium,
    css`
      font-size: 1em;
    `,
  )}

  ${RcRatingWrapper} {
    float: left;
    margin-right: 0.5em;
  }
`;

export const ReplyStatus = styled.span<{ hasReply?: boolean }>`
  color: ${({ hasReply }) =>
    hasReply ? colors.richElectricBlue : colors.dimGray};
  font-size: 14px;
  margin-right: 12px;
`;

type ReplyLoveType = {
  actived?: boolean;
  icon: string;
  iconHover?: string;
};
export const ReplyLove = styled.span<ReplyLoveType>`
  display: inline-block;
  background: url("${({ icon }) => icon}") no-repeat center left / 12px auto;
  background-position-y: 2px;
  color: ${colors.dimGray};
  font-size: 14px;
  padding-left: 18px;
  cursor: pointer;
  
  &:hover {
    color: ${colors.richElectricBlue};
    background: url("${({ icon, iconHover }) =>
      iconHover ? iconHover : icon}") no-repeat center left / 12px auto;
  }
    
  ${({ actived, iconHover }) =>
    actived &&
    css`
      color: ${colors.richElectricBlue};
      background: url("${iconHover}") no-repeat center left / 12px auto;
    `}
  `;

export const ReplyDuration = styled.span`
  color: ${colors.dimGray};
  margin-left: 12px;
  font-size: 14px;
`;

export const ReplyDivider = styled.span`
  color: ${colors.dimGray};
  margin-left: 12px;
  margin-top: -3px;
`;

export const ClientCommentReviewStatusBar = styled.div`
  ${flexCss};
  line-height: 1;
`;

export const SearchBarButton = styled(Button)`
  margin-left: 1em;
  height: 38px !important;
`;

export const SearchBarWrapper = styled.div`
  position: relative;
  border: 1px solid #e7e7e7;
  padding: 6px 0 6px 12px;

  ${maxQuery(
    SizeMax.Small,
    css`
      margin-top: 0.25em;
    `,
  )}

  ${SearchBarButton} {
    position: absolute;
    right: 0;
    top: 50%;
    transform: translateY(-50%);
  }
`;

export const PaginationExtened = styled(Pagination)`
  margin-top: 1.5em;
`;

export const InputReview = styled(Textfield)`
  margin-top: 1em;
  font-size: 12px;
  max-width: 610px;
`;

export const TextExtended = styled(Text)`
  padding-top: 1em;
`;

// Customer List Review Modal:
export const ContentBlock = styled.div`
  display: flex;

  ${maxQuery(
    SizeMax.Small,
    css`
      flex-direction: column;
      align-items: flex-start;
    `,
  )}
`;

export const UIPlaceholderExtended = styled(UIPlaceholder)``;
export const RatingImageSlider = styled.div`
  width: 100%;
  margin-bottom: 1em;

  ${UIPlaceholderExtended} {
    height: 100%;
  }

  ${minQuery(
    SizeMin.Small,
    css`
      width: 50%;
    `,
  )}
`;

export const RatingInformationDetailModalTitle = styled(
  ClientCommentReviewTitle,
)``;
export const RatingInformation = styled.div`
  ${minQuery(
    SizeMin.Small,
    css`
      width: 50%;
      margin-left: 1em;
    `,
  )};

  ${RatingInformationDetailModalTitle} {
    margin-bottom: 0.5em;
  }

  ${ClientCommentReviewTitle} {
    ${flexCss};
    flex-wrap: wrap;
  }
`;

export const ModalBodyInner = styled.div`
  padding: 24px;
`;

export const ActionAndDatingBlock = styled.div``;
export const CommentBlock = styled.div`
  ${RatingInformationDetailModalTitle}, ${ClientCommentReviewContent} {
    margin-bottom: 0.5em;
  }
  ${ClientCommentReviewItem} {
    padding-left: 1.5em;

    ${ClientCommentReviewTitle} {
      ${flexCss};
      flex-wrap: wrap;
    }
  }
`;

export const CustomerListReviewModal = styled(CustomerReviewModal)`
  @media (min-width: 768px) {
    .o-modal_content {
      min-width: 758px;
    }
  }
`;

export const STab = styled(Tab)<{ selected?: boolean }>`
  list-style: none;
  cursor: pointer;
  text-transform: uppercase;
  border: none;
  display: inline-block;
  color: ${colors.dimGray};
  font-weight: bold;
  padding: 2.5px 5px;
  
  ${maxQuery(
    SizeMax.Small,
    css`
      font-size: 11px;
      text-align: center;
    `,
  )}

  ${minQuery(
    SizeMin.Small,
    css`
      padding: 5px 15px;
    `,
  )}

  ${({ selected }): SimpleInterpolation =>
    selected
      ? css`
          border-bottom: 2px solid ${colors.richElectricBlue};
          color: ${colors.richElectricBlue};
        `
      : css`
          border-bottom: 1px solid #d5d5d5;
        `}

  &:hover {
    opacity: 0.75;
  }
`;

export const ReviewItem = styled.div<{ isVideoItem?: boolean; ratio?: number }>`
  width: calc(100% / 3);
  list-style: none;
  overflow: hidden;
  cursor: pointer;
  position: relative;

  ${({ ratio }) =>
    ratio
      ? css`
          &:after {
            content: "";
            display: block;
            padding-bottom: ${ratio * 100}%;
          }
        `
      : css``}

  ${({ isVideoItem }) =>
    isVideoItem &&
    css`
      &:before {
        content: "▶";
        color: #d5d5d5;
        position: absolute;
        font-size: 32px;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%) scale(1);
        transition: 0.45s cubic-bezier(0.075, 0.82, 0.165, 1);
        border: 2px solid #d5d5d5;
        border-radius: 50%;
        width: 40px;
        height: 40px;
        display: flex;
        align-items: center;
        justify-content: center;
        padding-left: 7px;
        padding-top: 3px;
      }

      &:hover {
        &:before {
          transform: translate(-50%, -50%) scale(1.1);
          color: ${colors.richElectricBlue};
          border: 2px solid ${colors.richElectricBlue};
        }
      }
    `}
`;

export const ReviewItemInner = styled.div`
  position: absolute;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  width: 100%;
  height: 100%;
`;

export const ReviewItemImgWrapper = styled.div`
  width: 100%;
  height: 84.555984555984556%;
`;
// 84.555984555984556%; (219 * 100) / 259

export const ReviewItemImg = styled.div<{ src: string }>`
    width: 100%;
    height: 100%;
    background: url("${({ src }) => src}") no-repeat center center / contain;
    transform: scale(1);
    transition: .75s;
  `;

export const BaseStarIcon = styled.span<{ icon: string }>`
  display: inline-block;
`;
export const ActiveStar = styled(BaseStarIcon)`
  background: url("${activeStarIcon}") no-repeat center center / contain;
`;

export const DeActiveStar = styled(BaseStarIcon)`
  background: url("${deActiveStarIcon}") no-repeat center center / contain;
`;

export const ReviewItemRating = styled.div`
  display: flex;
  justify-content: center;
  height: 8.328185328185328%;
  padding-bottom: .25em;

  ${ActiveStar},${DeActiveStar} {
    width: calc(100% / 5);
  }
`;
// 8.328185328185328%; (21.57 * 100) / 259

export const AnimateBox = styled.div`
  animation: slideUp 1.35s cubic-bezier(0.075, 0.82, 0.165, 1);

  @keyframes slideUp {
    from {
      opacity: 0;
      transform: translateY(25px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

export const InfiniteScrollExtendedOuter = styled.div`
  overflow: auto;
  display: flex;
  width: 100%;
  justify-content: space-between;
  flex-wrap: wrap;
  padding: 0 1em;

  ${ReviewItem} {
    width: calc(100% / 3);
    margin-bottom: 0.9375em;

    ${ReviewItemImgWrapper} {
      overflow: hidden;

      ${ReviewItemImg} {
      }
    }

    ${ReviewItemRating} {
    }

    &:hover {
      background-color: #edf5f9;
      box-shadow: 0px 3px 6px ${alphaColor.black016};

      ${ReviewItemImgWrapper} {
        ${ReviewItemImg} {
          transform: scale(1.065);
        }
      }
    }
  }
`;

export const VideoPanelSearchItem = styled(VideoPanel)``;
export const VideoPanelPreviewItem = styled(VideoPanel)``;
export const RatingSection = styled.section`
  position: relative;
`;

export const LoggedUserInfoBar = styled.div`
  display: flex;
  width: 100%;
  margin-bottom: 1em;
  font-size: 12px;
`;

export const LoggedUserInfo = styled.div`
  flex: 1;
  ${flexCss};

  > span {
    margin-left: 0.5em;
  }
`;

export const OtherAction = styled.div`
  flex: 1;
  ${flexCss};
  justify-content: flex-end;

  > span {
    white-space: nowrap;
  }
`;

export const LogoutTextLink = styled.span`
  color: ${colors.richElectricBlue};
  margin-left: 0.25em;

  &:hover {
    opacity: 0.75;
  }
`;

export const FormInputUserUnLoggingInputWrapper = styled.div``;

export const FormInputUserUnLoggingTextfield = styled(Textfield)`
  input {
    font-size: 12px;
  }
`;

export const FormInputUserUnLogging = styled.div`
  ${flexCss};
  justify-content: space-between;
  margin-bottom: 1em;

  ${maxQuery(
    SizeMax.Small,
    css`
      ${FormInputUserUnLoggingInputWrapper} {
        margin-top: 1em;

        &:first-of-type {
          margin-top: 0;
        }
      }
    `,
  )}
`;

export const SubmitBlock = styled.div`
  ${flexCss};
  justify-content: center;
  padding: 2em 1.5em;
`;
